import { DestroyRef, inject, Injectable, signal, WritableSignal } from '@angular/core';
import { catchError, map, Observable, of, switchMap, tap } from 'rxjs';

import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { EMPTY_LIST_RESPONSE, ListResponse, SqlAggregateFunctionType, SysAdminUiInfo } from '@models/helpers';
import { GlobalFilterKeys } from '@models/module/fields/enums';
import { DataListType } from '@models/module/fields/main/dataListFieldModel';
import { FilterLogic, GridSearchEntityInstance, OrderType, SearchViewModel } from '@models/module/grid';
import { GridFieldsModel } from '@models/module/main';
import { ModuleGridService, ModuleInstanceService } from '@module-instance/services';
import {
  CipoTableOptions,
  EntityTableRow,
  FiltersToApply,
  FooterRow,
  LocalFilterService,
  TableDataService,
} from '@shared/components/data-list/common';
import { HttpService } from '@shared/services';
import { BREAKPOINTS } from 'src/app-breakpoints.module';
import {
  DmFiltersToApply,
  DmQueryParams,
  DmTableDataRequest,
  DmTableDataResponse,
  EntityInstanceTransition,
  HeaderFooterData,
  NotifyEmailsAndRolesModel,
  RelationPropsType,
  WbsParentModel,
} from '../dm.model';

@Injectable({ providedIn: 'root' })
export class DmService {
  private http = inject(HttpService);
  private localFiltersService = inject(LocalFilterService);
  private moduleInstanceService = inject(ModuleInstanceService);
  private moduleGridService = inject(ModuleGridService);
  private tableDataService = inject(TableDataService);
  private destroyRef = inject(DestroyRef);

  readonly gridSearchDebugInfo = signal<SysAdminUiInfo>(null);

  clearGridSearchDebugInfo() {
    this.gridSearchDebugInfo.set(null);
  }

  getGridFields({ moduleId, contractId, workflowId, relationProps, dataListType }: DmQueryParams) {
    return this.moduleGridService.getGridFields({
      contractId,
      moduleId,
      relationId: relationProps?.relationId,
      workflowId,
      dataListType: relationProps?.relationId ? DataListType.Relation : dataListType,
    });
  }

  getTableData(request: DmTableDataRequest): Observable<DmTableDataResponse> {
    if (!request.initialLoad) {
      return this.getGridSearchData(request);
    }

    return this.getGridFields(request).pipe(
      switchMap(gridFields => {
        const search = this.getInitialLoadSearch(request.search, gridFields);
        return this.getGridSearchData({ ...request, search }).pipe(
          map(response => ({
            ...response,
            gridFields,
            search,
          })),
        );
      }),
    );
  }

  private getGridSearchData({
    contractId,
    moduleId,
    search,
    relationProps,
    dataListType,
  }: DmTableDataRequest): Observable<ListResponse<GridSearchEntityInstance>> {
    let gridSearch: Observable<ListResponse<GridSearchEntityInstance>>;
    if (relationProps) {
      const { parentEntityInstanceId, relationId, fieldId } = relationProps;
      gridSearch = this.moduleGridService.gridSearchChildren({
        body: search,
        contractId,
        moduleId,
        relationId,
        parentEntityInstanceId,
        fieldId,
      });
    } else {
      gridSearch = this.moduleGridService.gridSearch({
        body: search,
        contractId,
        moduleId,
        dataListType,
      });
    }

    return gridSearch.pipe(
      tap(response => this.gridSearchDebugInfo.set(response?.sysAdminUIInfo ?? null)),
      catchError(() => {
        this.gridSearchDebugInfo.set(null);
        return of(EMPTY_LIST_RESPONSE);
      }),
    );
  }

  private getInitialLoadSearch(search: SearchViewModel, gridFields: GridFieldsModel): SearchViewModel {
    const defaultSort = this.tableDataService.getDefaultSortOnColumns(gridFields.fields ?? []);
    const active = defaultSort?.active ?? 'full_doc_num';
    const direction = defaultSort?.direction ?? 'desc';
    return {
      ...search,
      page: 1,
      sort: [
        {
          field: active,
          order: direction === 'asc' ? OrderType.Ascending : OrderType.Descending,
        },
      ],
    };
  }

  //   TODO: pagination and sorting are irelevant here
  getHeaderFooterData(
    contractId: number,
    moduleId: number,
    searchParams: SearchViewModel,
    relationProps?: RelationPropsType,
  ) {
    let gridExpressions: Observable<HeaderFooterData>;
    if (relationProps) {
      const { parentEntityInstanceId, relationId } = relationProps;
      gridExpressions = this.moduleGridService.gridExpresionsChildren({
        contractId,
        moduleId,
        body: searchParams,
        relationId,
        parentEntityInstanceId,
      });
    } else {
      gridExpressions = this.moduleGridService.gridExpressions({
        contractId,
        moduleId,
        body: searchParams,
      });
    }
    return gridExpressions.pipe(
      map(({ bottomFormulaRows, ...data }) => ({
        ...data,
        bottomFormulaRows: bottomFormulaRows.map(row => ({
          ...row,
          label: SqlAggregateFunctionType[row.key],
          empty: false,
        })),
      })),
    );
  }

  getAndSetHeaderFooterData(
    contractId: number,
    moduleId: number,
    relationProps: RelationPropsType,
    gridFields: GridFieldsModel,
    tableOptions: CipoTableOptions,
    filtersToApply: DmFiltersToApply,
    topFormulasSetter: WritableSignal<Record<string, unknown>>,
    bottomFormulasSetter: WritableSignal<FooterRow[]>,
  ) {
    if (!gridFields) {
      return;
    }
    const { bottomFormulas, topFormulas } = gridFields;
    const isSmallscreen = window.innerWidth < BREAKPOINTS.sm;
    if ((bottomFormulas?.length || topFormulas?.length) && !isSmallscreen) {
      this.getHeaderFooterData(contractId, moduleId, this.getSearchParams(tableOptions, filtersToApply), relationProps)
        .pipe(takeUntilDestroyed(this.destroyRef))
        .subscribe({
          next: ({ bottomFormulaRows, topFormulas }) => {
            topFormulasSetter.set(topFormulas);
            bottomFormulasSetter.set(bottomFormulaRows);
          },
          error: () => {
            bottomFormulasSetter.update(bottomFormulaRows => bottomFormulaRows.map(row => ({ ...row, empty: false })));
          },
        });
    }
  }

  getMultiRowsData({
    contractId,
    moduleId,
    entityInstanceIds,
    isCurrent,
    relationProps,
  }: DmQueryParams & { entityInstanceIds: number[] }) {
    // Please, let's implement another endpoint for one row data, to avoid this sh!t
    const searchParams: SearchViewModel = {
      criteria: '',
      page: 1,
      pagesize: 10,
      sort: [{ field: 'full_doc_num', order: 1 }],
      isCurrent: isCurrent ?? true,
      filter: {
        logic: FilterLogic.OR,
        filters: entityInstanceIds.map(id => ({
          logic: FilterLogic.AND,
          filters: [
            {
              field: '-1',
              operator: 0,
              options: '',
              value: id,
              fieldTypeId: 2,
            },
          ],
        })),
      },
    };
    return this.getTableData({ contractId, moduleId, search: searchParams, relationProps });
  }

  getMultipleTransitions(
    rows: (GridSearchEntityInstance | EntityTableRow)[],
    currentTransitions: Record<string, EntityInstanceTransition[]>,
    { contractId, workflowId, moduleId }: DmQueryParams,
  ): Observable<Record<string, EntityInstanceTransition[]>> {
    if (!rows?.length) {
      return of({});
    }

    const states = rows.reduce(
      (acc, row) => {
        if (row.needs_approval) {
          /* do not take into consideration approvals - these are drafts too, but without transitions */
          return acc;
        }
        const stateProp = String(row.current_state_id);
        if (!acc[stateProp]) {
          /* Skip rows that have an open revision: the document itself is frozen and has no transitions. */
          if (row.has_revisions) {
            return acc;
          }
          acc[stateProp] = row.entity_instance_id ?? 0;
        }
        return acc;
      },
      {} as Record<string, number>,
    );
    const cachedTransitions = currentTransitions || {};
    // Only fetch states that don't have cached transitions yet
    const statesWithoutTransitions: Record<string, number> = {};
    for (const key in states) {
      if (!cachedTransitions[key]) {
        statesWithoutTransitions[key] = states[key];
      }
    }

    const entityInstances = Object.entries(statesWithoutTransitions).reduce(
      (acc, [stateName, entityInstanceId]) => [...acc, { stateName, entityInstanceId }],
      [] as { stateName: string; entityInstanceId: number }[],
    );
    const entityInstanceIds = entityInstances.map(ei => ei.entityInstanceId);
    if (!entityInstanceIds.length) {
      /* All states already have cached transitions, or no valid states found */
      return of(cachedTransitions);
    }
    return this.moduleInstanceService
      .getMultipleTransitions({
        entityInstanceIds,
        contractId,
        workflowId,
        moduleId,
        onlyUsedInMultiple: true,
      })
      .pipe(
        map(multipleTransitionsList => {
          const fetchedTransitions = (multipleTransitionsList ?? []).reduce(
            (acc, multipleTransitions) => {
              const stateName = entityInstances.find(
                ei => ei.entityInstanceId === multipleTransitions.entityInstanceId,
              )?.stateName;
              if (stateName && multipleTransitions.transitions?.length) {
                acc[stateName] = multipleTransitions.transitions;
              }
              return acc;
            },
            {} as Record<string, EntityInstanceTransition[]>,
          );
          // Merge fetched transitions with already-cached ones
          return { ...cachedTransitions, ...fetchedTransitions };
        }),
      );
  }

  //   TODO: Implement properly the deleteMultiple API, so we will use this one only
  deleteDocuments(rows: EntityTableRow[]) {
    const {
      module_id: moduleId,
      contract_id: contractId,
      entity_id: entityId,
      entity_instance_id: entityInstanceId,
    } = rows[0];
    if (rows.length === 1) {
      return this.moduleInstanceService.deleteSingle({
        contractId,
        moduleId,
        entityInstanceId,
      });
    } else {
      const ids = rows.map(row => row.entity_instance_id);
      return this.moduleInstanceService.deleteMultiple({
        moduleId,
        entityId,
        ids,
      });
    }
  }

  //   TODO: When deleteDocuments will only be multiple, we can remove this method
  deleteMultipleDocuments(rows: EntityTableRow[]) {
    const { module_id: moduleId, entity_id: entityId } = rows[0];
    const ids = rows.map(row => row.entity_instance_id);
    return this.moduleInstanceService.deleteMultiple({
      moduleId,
      entityId,
      ids,
    });
  }

  getAsExcel(contractId: number, moduleId: number, searchParams: SearchViewModel, dataListType?: DataListType) {
    return this.moduleInstanceService.getAsExcel({
      contractId,
      moduleId,
      body: searchParams,
      dataListType,
    });
  }

  undoLastAction(
    rows: EntityTableRow[],
    { contractId, moduleId }: DmQueryParams,
    notifyUsers: NotifyEmailsAndRolesModel,
  ) {
    const ids = rows.map(row => row.entity_instance_id);
    return this.moduleInstanceService.undoLastAction({ contractId, ids, moduleId, notifyUsers });
  }

  getWbsParents() {
    return this.http.get<WbsParentModel[]>('_api/WBS/Parents', { useLoader: false });
  }

  deleteWbs(sourceProjectId?: string) {
    const params = this.http.buildHttpParams({ sourceProjectId });
    return this.http.delete<void>('_api/WBS/DeleteWbs', {
      httpOptions: { params },
      useLoader: false,
    });
  }

  getGlobalFilter(contractId: number, id: GlobalFilterKeys, moduleId: number): Observable<FiltersToApply> {
    return this.moduleGridService.getGlobalFilter({ contractId, id, moduleId }).pipe(
      map(({ filters }) => {
        const { dataSource, field, fieldTypeId, operator, value } = filters[0];
        const mappedFilters: FiltersToApply = {
          filters: {
            filterList: [
              {
                field,
                operator,
                value: dataSource ? dataSource.map(({ key }) => key) : value,
                fieldTypeId,
                dataSource,
              },
            ],
          },
        };
        return mappedFilters;
      }),
    );
  }

  getSearchParams(options: CipoTableOptions, filters: DmFiltersToApply, pageIndex = 0): SearchViewModel {
    const searchParams: SearchViewModel = {
      page: pageIndex + 1,
      pagesize: options.pagination.pageSize,
      filter: this.localFiltersService.mapLocalFilters(filters.filters),
      criteria: filters.criteria,
      isCurrent: filters.isCurrent ?? true,
      showPresets: filters.showPresets ?? false,
      sort: options.sort && [
        {
          field: options.sort.active,
          order: options.sort.direction === 'asc' ? OrderType.Ascending : OrderType.Descending,
        },
      ],
    };

    return searchParams;
  }
}
